import * as React from "react";
import { connect } from "common/connect";
import StoreKeys from "common/storeKeys";
import styled from "styled-components";
import { Colors } from "styles/Colors";
import Store from "common/store";
// eslint-disable-next-line no-unused-vars
import { EventFormatData, getOpenStageIndices } from "common/helpers/eventFormatHelper";
import { EventStageTypes } from "common/constants/EventStageTypes";
import BackToMyPicks from "common/components/EventControls/BackToMyPicks";
import PicksHelper from "common/picksHelper";

const propKeys = {
	eventData: StoreKeys.EVENT.EVENTDATA,
	format: StoreKeys.EVENT.FORMAT,
	stageIndex: StoreKeys.EVENT.STAGE_INDEX,
	substageIndex: StoreKeys.EVENT.SUBSTAGE_INDEX,
	view: StoreKeys.EVENT.VIEW,
	bracketUser: StoreKeys.EVENT.BRACKET_USER,
	hasBracket: StoreKeys.EVENT.HAS_BRACKET,
	submittedStageIds: StoreKeys.EVENT.USER.SUBMITTED_STAGE_IDS,
};

/**
 * @typedef StageSelectorCProps
 * @type {object}
 * @property {any} eventData
 * @property {EventFormatData} format
 * @property {number} stageIndex
 * @property {number} substageIndex
 */

const StageLabel = styled.label`
	margin: auto;
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	line-height: 1.2;
	// two lines tall always, so a wrapping name doesn't make its chip taller than the rest
	min-height: 2.4em;
`;

const StageButton = styled.div`
	display: flex;
	width: 100px;
	margin: 4px;
	padding: 8px 12px;
	border-radius: 12px;
	text-align: center;
	border: 1px solid
		${({ highlighted }) => (highlighted ? Colors.pickstop : "transparent")};
	background: ${({ selected }) => (selected ? Colors.primary : Colors.panel)};
	color: ${({ selected, unlocked }) =>
		selected
			? Colors.panel
			: unlocked
			? Colors.primary
			: Colors.panelLighterColor};
	cursor: ${({ unlocked }) => (unlocked ? "pointer" : "default")};
	font-weight: 400;
	overflow-wrap: break-word;
	align-content: center;
	justify-content: center;
	transition: background 120ms ease;
	// the browser's tap flash ignores border-radius, so we paint our own
	-webkit-tap-highlight-color: transparent;

	@media (max-width: 599.95px) {
		box-sizing: border-box;
		width: 92px;
		padding: 8px 6px;
	}

	&:hover {
		background: ${({ selected, unlocked }) =>
			selected || !unlocked ? undefined : Colors.panelLight};
	}

	${StageLabel} {
		cursor: ${({ unlocked }) => (unlocked ? "pointer" : "default")};
	}
`;

const StageRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	margin: ${({ spaced }) => (spaced ? "8px 0 6px" : "0 0 8px")};
`;

const ChipGroup = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
`;

const SubstageRow = styled(ChipGroup)`
	margin-bottom: 8px;
`;

const BackSlot = styled.div`
	display: flex;
	align-items: center;
	margin: 4px;
`;

/** Breaks only before words over 3 chars, so "Round of 32" keeps "of 32" on one line. */
function stickyShortWords(text) {
	return (text ?? "")
		.split(" ")
		.reduce(
			(acc, word, i) =>
				i === 0 ? word : acc + (word.length > 3 ? " " : "\u00A0") + word,
			""
		);
}

/**
 * @param {StageSelectorCProps} props
 */
function StageSelectorC({
	eventData,
	format,
	stageIndex,
	substageIndex,
	view,
	bracketUser,
	hasBracket,
	submittedStageIds,
	disableOverview,
}) {
	const currentStage = format.stages[stageIndex];
	/** @type {EventStage} */
	const currentEventStage = eventData.eventStages[stageIndex];

	const championEnabled = !disableOverview && !!format.championPick?.enabled;
	const isOverview = championEnabled && view === "overview";

	const picksLocked = new PicksHelper(eventData).isLocked();

	const showBackToMyPicks =
		isOverview && !!bracketUser && (!picksLocked || hasBracket);

	// Single-stage events still need the selector when the Overview chip exists.
	if (format.stages.length <= 1 && !championEnabled) {
		return <></>;
	}

	const openStages = getOpenStageIndices(eventData, format);

	// Past and current stages, every open one, and side stages, which are always browsable.
	const stageUnlocked = (i) =>
		i <= eventData.stage ||
		openStages.includes(i) ||
		!!format.stages[i]?.sideStage;

	// One open stage is normal, no cue needed.
	const highlightOpen = openStages.length > 1;

	const changeStage = (i) => {
		if (isOverview) {
			Store.set(StoreKeys.EVENT.VIEW, "picks");
		}

		if (i === stageIndex) {
			return;
		}

		if (!stageUnlocked(i)) {
			return;
		}

		const unpicked = !(submittedStageIds ?? []).includes(
			eventData.eventStages?.[i]?.id
		);

		const updates = {};
		updates[StoreKeys.EVENT.STAGE_INDEX] = i;
		updates[StoreKeys.EVENT.SUBSTAGE_INDEX] = 0;
		updates[StoreKeys.EVENT.IS_EDITING] =
			!bracketUser && openStages.includes(i) && unpicked;
		updates[StoreKeys.EVENT.STEP] = 0;

		Store.setMany(updates);
	};

	const changeSubstage = (i) => {
		if (i === substageIndex) {
			return;
		}

		if (i >= currentEventStage.eventSubstage.length) {
			return;
		}

		Store.set(StoreKeys.EVENT.SUBSTAGE_INDEX, i);
	};

	const isGroupStageBrackets =
		currentStage?.type === EventStageTypes.Group &&
		currentStage?.groupStageDetails?.hasGroupBrackets;

	return (
		<>
			<StageRow spaced={championEnabled}>
				{showBackToMyPicks && (
					<BackSlot>
						<BackToMyPicks />
					</BackSlot>
				)}
				<ChipGroup>
					{championEnabled && (
						<StageButton
							selected={isOverview}
							unlocked={true}
							onClick={() => Store.set(StoreKeys.EVENT.VIEW, "overview")}
							key="overview"
						>
							<StageLabel>Overview</StageLabel>
						</StageButton>
					)}
					{format.stages.map((s, i) => {
						const selected = i === stageIndex && !isOverview;
						const unlocked = stageUnlocked(i);
						return (
							<StageButton
								selected={selected}
								unlocked={unlocked}
								highlighted={highlightOpen && openStages.includes(i)}
								onClick={() => unlocked && changeStage(i)}
								key={i}
							>
								<StageLabel>{stickyShortWords(s.name)}</StageLabel>
							</StageButton>
						);
					})}
				</ChipGroup>
			</StageRow>

			{!isOverview && isGroupStageBrackets && currentEventStage?.eventSubstage?.length > 1 && (
				<SubstageRow>
					{currentEventStage.eventSubstage.map((ss, i) => {
						const selected = i === substageIndex;
						return (
							<StageButton
								selected={selected}
								unlocked={true}
								onClick={() => changeSubstage(i)}
								key={i}
							>
								<StageLabel>{stickyShortWords(ss.title)}</StageLabel>
							</StageButton>
						);
					})}
				</SubstageRow>
			)}
		</>
	);
}

export default function StageSelector(props) {
	return connect(<StageSelectorC />, propKeys, props);
}
